import {useId} from 'react'
import {Label, type InputProps} from '~/design-system/foundations'
import HgTooltip from '../HgTooltip'
import MercuryCleave from '~/design-system/foundations/MercuryCleave'
import {type CleaveOptions} from 'cleave.js/options'
import HgIcon from '../HgIcon'
import {tv} from 'tailwind-variants'
import {HgButton} from '../HgButton'
import {type IconOnlyButtonProps} from '../HgButton/types'

export type HgTextInputProps = {
  label: React.ReactNode
  secondaryLabel?: string
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  tooltip?: React.ReactNode
  cleaveOptions?: CleaveOptions
  errorMessage?: string
  buttonProps?: Omit<IconOnlyButtonProps, 'contentType' | 'size' | 'variant'>
} & Omit<InputProps, 'id'>

const inputVariants = tv({
  base: 'h-full bg-transparent-transparent py-8 placeholder:text-text-subdued hover:bg-transparent-transparent focus-visible:outline-none',
  variants: {
    hasPrefix: {
      true: 'pl-s1',
      false: 'pl-12',
    },
    hasSuffix: {
      true: 'pr-s1',
      false: 'pr-12',
    },
  },
})

export default function HgTextInput({
  label,
  secondaryLabel,
  tooltip,
  prefix,
  suffix,
  cleaveOptions,
  errorMessage,
  buttonProps,
  ...props
}: HgTextInputProps) {
  const id = useId()
  const errorId = useId()
  const hasError = !!errorMessage
  return (
    <div className="group flex max-w-[256px] flex-col gap-s2 arcadia-ui-2">
      <Label
        htmlFor={id}
        className="flex items-center gap-s1 text-text-subdued group-focus-within:text-text-default group-hover:text-text-default group-has-[:disabled]:text-text-disabled group-has-[input[aria-invalid=true]]:text-text-error"
      >
        {label}
        {tooltip && (
          <HgTooltip
            content={tooltip}
            classNames={{
              trigger:
                'text-inherit group-has-[input[aria-invalid=true]]:text-text-error',
            }}
          />
        )}
      </Label>
      <div className="flex h-40 w-full items-center justify-between rounded-lg border border-solid border-border-input bg-surface-input text-text-default has-[input:disabled]:bg-surface-input-disabled has-[input:focus]:bg-surface-input-active has-[input:focus]:outline has-[input:focus]:outline-2 has-[input:focus]:outline-border-focus group-has-[input[aria-invalid=true]]:outline group-has-[input[aria-invalid=true]]:outline-1 group-has-[input[aria-invalid=true]]:outline-text-error group-hover:[&:has(input:not(:disabled))]:bg-surface-input-hover group-hover:[&:has(input:not(:disabled))]:shadow-[0_1px_8px_0_rgba(111,115,150,0.1)]">
        <div className="flex h-full items-center">
          {prefix && <span className="pl-12 text-text-disabled">{prefix}</span>}
          <MercuryCleave
            aria-invalid={hasError}
            aria-errormessage={errorId}
            options={cleaveOptions || {}}
            id={id}
            className={inputVariants({
              hasPrefix: !!prefix,
              hasSuffix: !!suffix,
            })}
            {...props}
          />
        </div>
        {suffix && <span className="pr-12 text-text-disabled">{suffix}</span>}
        {buttonProps && (
          <HgButton
            contentType="icon"
            variant="text"
            size="xs"
            className="mr-12 [&:not(:focus-visible)]:outline-0"
            {...buttonProps}
          />
        )}
      </div>
      {secondaryLabel && (
        <Label htmlFor={id} className="text-text-subdued">
          {secondaryLabel}
        </Label>
      )}
      <span
        id={errorId}
        className="hidden items-center gap-s1 text-text-error group-has-[input[aria-invalid=true]]:flex"
      >
        <HgIcon iconType="error" size="small" /> {errorMessage}
      </span>
    </div>
  )
}
